/**
 * pages/PlanAhead.js — Phase 5A.1.
 *
 * Three top tabs: Overview · Goals · Projections. URL deep-linkable via
 * `?tab=`. Goals reuses the existing pages/Goals.js verbatim via the
 * `embedded` prop — all CRUD, all templates, all live signals continue
 * to work.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import usePageTitle from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import ReportingCurrencyPrompt from '@/components/ReportingCurrencyPrompt';
import Goals from '@/pages/Goals';
import PlanAheadOverviewTab    from '@/components/plan-ahead/PlanAheadOverviewTab';
import PlanAheadProjectionsTab from '@/components/plan-ahead/PlanAheadProjectionsTab';

const TOP_TABS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'goals',       label: 'Goals' },
  { id: 'projections', label: 'Projections' },
];
const VALID = new Set(TOP_TABS.map((t) => t.id));

export default function PlanAhead() {
  usePageTitle('Plan Ahead');
  const { profile, session, refreshProfile } = useAuth();
  // A fallback is not a choice. When nobody has told us the reporting currency
  // we ask (see ReportingCurrencyPrompt) instead of quietly showing rupees —
  // which is what a beta user with no stated currency was shown on 2026-08-26.
  const baseCurrency = profile?.base_currency || profile?.currency || 'INR';
  // `profiles.currency` is defaulted to 'INR' by the database for every new
  // signup, so it proves nothing about what the person wants. Only
  // `base_currency` records an actual choice, so that is what we test.
  const currencyUnstated = !profile?.base_currency;

  const [searchParams, setSearchParams] = useSearchParams();
  const initial = VALID.has(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [section, setSection] = useState(initial);

  useEffect(() => {
    const cur = searchParams.get('tab');
    if (section === 'overview' && cur) setSearchParams({}, { replace: true });
    else if (section !== 'overview' && cur !== section) setSearchParams({ tab: section }, { replace: true });
     
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = searchParams.get('tab');
    if (VALID.has(t) && t !== section) setSection(t);
    else if (!t && section !== 'overview') setSection('overview');
     
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-6 sm:pt-8 pb-16" data-testid="plan-ahead-page">
      <div className="mb-6">
        <h1 className="font-fraunces text-2xl md:text-[28px] text-charcoal font-medium leading-tight" data-testid="plan-ahead-title">Plan Ahead</h1>
        <p className="text-muted-foreground text-sm mt-1">Where your money is heading, and what to review next.</p>
      </div>

      {currencyUnstated && (
        <div className="mb-6">
          <ReportingCurrencyPrompt
            profile={profile}
            token={session?.access_token}
            onSaved={refreshProfile}
          />
        </div>
      )}

      {/* Top-level tabs */}
      <div className="flex items-center gap-0 border-b border-border/60 mb-6 overflow-x-auto" role="tablist">
        {TOP_TABS.map((t) => {
          const isActive = section === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSection(t.id)}
              className={
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors relative whitespace-nowrap ' +
                (isActive ? 'text-evergreen' : 'text-muted-foreground hover:text-foreground')
              }
              role="tab"
              aria-selected={isActive}
              data-testid={`plan-ahead-tab-${t.id}`}
            >
              {t.label}
              {isActive && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-evergreen rounded-t-sm" />}
            </button>
          );
        })}
      </div>

      {section === 'overview' && (
        <PlanAheadOverviewTab
          baseCurrency={baseCurrency}
          onGoToGoals={() => setSection('goals')}
          onGoToProjections={() => setSection('projections')}
        />
      )}
      {section === 'goals'       && <Goals embedded />}
      {section === 'projections' && <PlanAheadProjectionsTab baseCurrency={baseCurrency} />}
    </div>
  );
}
